import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IsActiveMatchOptions, RouterLink, RouterLinkActive } from '@angular/router';
import { FakturniaHorizontalNavigationComponent } from '@fakturnia/components/navigation/horizontal/horizontal.component';
import { FakturniaNavigationService } from '@fakturnia/components/navigation/navigation.service';
import { FakturniaNavigationItem } from '@fakturnia/components/navigation/navigation.types';
import { FakturniaUtilsService } from '@fakturnia/services/utils/utils.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector       : 'fakturnia-horizontal-navigation-basic-item',
    templateUrl    : './basic.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone     : true,
    imports        : [NgClass, NgIf, NgFor, RouterLink, RouterLinkActive, MatTooltipModule, NgTemplateOutlet, MatMenuModule, MatIconModule],
})
export class FakturniaHorizontalNavigationBasicItemComponent implements OnInit, OnDestroy
{
    @Input() item: FakturniaNavigationItem;
    @Input() name: string;

    @ViewChild('menuTrigger') menuTrigger: MatMenuTrigger;
    menuCloseTimer: any;
    
    isActiveMatchOptions: IsActiveMatchOptions;
    private _fakturniaHorizontalNavigationComponent: FakturniaHorizontalNavigationComponent;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fakturniaNavigationService: FakturniaNavigationService,
        private _fakturniaUtilsService: FakturniaUtilsService,
    )
    {
        // Set the equivalent of {exact: false} as default for active match options.
        // We are not assigning the item.isActiveMatchOptions directly to the
        // [routerLinkActiveOptions] because if it's "undefined" initially, the router
        // will throw an error and stop working.
        this.isActiveMatchOptions = this._fakturniaUtilsService.subsetMatchOptions;
    }

    openMenu(): void {
        if (this.item.options && this.item.options.length > 0) {
            clearTimeout(this.menuCloseTimer);
            this.menuTrigger.openMenu();
        }
    }

    /**
     * Closes the menu after a short delay.
     */
    closeMenuWithDelay(): void {
        this.menuCloseTimer = setTimeout(() => {
            this.menuTrigger.closeMenu();
        }, 100);
    }

    /**
     * Cancels the pending menu close timer.
     */
    cancelMenuClose(): void {
        clearTimeout(this.menuCloseTimer);
    }

    /**
     * Closes the menu immediately.
     */
    closeMenuImmediately(): void {
        this.menuTrigger.closeMenu();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Set the "isActiveMatchOptions" either from item's
        // "isActiveMatchOptions" or the equivalent form of
        // item's "exactMatch" option
        this.isActiveMatchOptions =
            this.item.isActiveMatchOptions ?? this.item.exactMatch
                ? this._fakturniaUtilsService.exactMatchOptions
                : this._fakturniaUtilsService.subsetMatchOptions;

        // Get the parent navigation component
        this._fakturniaHorizontalNavigationComponent = this._fakturniaNavigationService.getComponent(this.name);

        // Mark for check
        this._changeDetectorRef.markForCheck();

        // Subscribe to onRefreshed on the navigation component
        this._fakturniaHorizontalNavigationComponent.onRefreshed.pipe(
            takeUntil(this._unsubscribeAll),
        ).subscribe(() =>
        {
            // Mark for check
            this._changeDetectorRef.markForCheck();
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
